import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getSetupIntent } from "../utils/api";
import "./PaymentReturn.css";

const REDIRECT_SECONDS = 5;
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 20000;

const SetupReturn = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);
  const [status, setStatus] = useState("loading");
  const [topupAmount, setTopupAmount] = useState(null);
  const [threshold, setThreshold] = useState(null);
  const intervalRef = useRef(null);
  const redirectStatus = searchParams.get("redirect_status");
  const setupIntentSecret = searchParams.get("setup_intent_client_secret");

  useEffect(() => {
    if (redirectStatus !== "succeeded" || !setupIntentSecret) {
      setStatus("error");
      return;
    }

    const setupIntentId = setupIntentSecret.split("_secret_")[0];
    let cancelled = false;

    const poll = async () => {
      const start = Date.now();
      while (Date.now() - start < POLL_TIMEOUT_MS) {
        try {
          const data = await getSetupIntent(setupIntentId);
          if (cancelled) return;
          if (data?.status === "succeeded") {
            if (data.topup_amount)
              setTopupAmount((data.topup_amount / 100).toFixed(2));
            if (data.threshold) setThreshold((data.threshold / 100).toFixed(2));
            setStatus("success");
            return;
          }
        } catch {
          if (!cancelled) setStatus("error");
          return;
        }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      }
      if (!cancelled) setStatus("error");
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [redirectStatus, setupIntentSecret]);

  useEffect(() => {
    if (status !== "success") return;
    intervalRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(intervalRef.current);
          navigate("/dashboard");
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [status, navigate]);

  const handleGoNow = () => {
    clearInterval(intervalRef.current);
    navigate("/dashboard");
  };

  if (status === "loading") {
    return (
      <div className="pr-page">
        <div className="pr-card">
          <div className="pr-spinner-wrap">
            <div className="pr-spinner" />
          </div>
          <p className="pr-loading-text">Confirming your setup…</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="pr-page">
        <div className="pr-card pr-card--error">
          <div className="pr-icon pr-icon--error">✕</div>
          <h1 className="pr-title">Setup Unsuccessful</h1>
          <p className="pr-subtitle">Something went wrong.</p>
          <button className="pr-btn" onClick={handleGoNow}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pr-page">
      <div className="pr-card">
        <div className="pr-icon-wrap">
          <svg className="pr-checkmark" viewBox="0 0 52 52">
            <circle
              className="pr-checkmark__circle"
              cx="26"
              cy="26"
              r="25"
              fill="none"
            />
            <path
              className="pr-checkmark__check"
              fill="none"
              d="M14 27l8 8 16-16"
            />
          </svg>
        </div>
        <h1 className="pr-title">Auto Top-up Activated</h1>
        <p className="pr-subtitle">
          Your payment method has been saved successfully.
        </p>
        <div className="pr-details">
          <div className="pr-detail-row">
            <span className="pr-detail-label">Status</span>
            <span className="pr-detail-value pr-detail-value--active">
              <span className="pr-status-dot" /> Active
            </span>
          </div>
          {topupAmount && (
            <div className="pr-detail-row">
              <span className="pr-detail-label">Top-up amount</span>
              <span className="pr-detail-value">£{topupAmount}</span>
            </div>
          )}
          {threshold && (
            <div className="pr-detail-row">
              <span className="pr-detail-label">Triggers below</span>
              <span className="pr-detail-value">£{threshold}</span>
            </div>
          )}
        </div>
        <button className="pr-btn" onClick={handleGoNow}>
          Go to Dashboard
        </button>
        <p className="pr-redirect-note">
          Redirecting in <span className="pr-countdown">{countdown}</span>s…
        </p>
      </div>
    </div>
  );
};

export default SetupReturn;
